export type StorageTier = 'durable' | 'session-only' | 'ephemeral';

export interface Storage {
  readonly persistence: StorageTier;
  get<T>(key: string, defaultValue: T): Promise<T>;
  set(key: string, value: unknown): Promise<void>;
  remove(key: string): Promise<void>;
}

interface Backend {
  tier: StorageTier;
  read(k: string): string | null;
  write(k: string, v: string): void;
  delete(k: string): void;
}

function probe(): Backend {
  const probeKey = '__fg_storage_probe__';

  // Tier 1: localStorage durable
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(probeKey, '1');
      localStorage.removeItem(probeKey);
      return {
        tier: 'durable',
        read: (k) => localStorage.getItem(k),
        write: (k, v) => localStorage.setItem(k, v),
        delete: (k) => localStorage.removeItem(k),
      };
    }
  } catch {
    /* fall through */
  }

  // Tier 2: sessionStorage
  try {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(probeKey, '1');
      sessionStorage.removeItem(probeKey);
      return {
        tier: 'session-only',
        read: (k) => sessionStorage.getItem(k),
        write: (k, v) => sessionStorage.setItem(k, v),
        delete: (k) => sessionStorage.removeItem(k),
      };
    }
  } catch {
    /* fall through */
  }

  // Tier 3: in-memory Map
  const mem = new Map<string, string>();
  return {
    tier: 'ephemeral',
    read: (k) => mem.get(k) ?? null,
    write: (k, v) => {
      mem.set(k, v);
    },
    delete: (k) => {
      mem.delete(k);
    },
  };
}

export function createStorage(namespace: string): Storage {
  const backend = probe();
  const ns = (key: string) => `${namespace}:${key}`;

  return {
    persistence: backend.tier,

    async get<T>(key: string, defaultValue: T): Promise<T> {
      const raw = backend.read(ns(key));
      if (raw === null) return defaultValue;
      try {
        return JSON.parse(raw) as T;
      } catch {
        return defaultValue;
      }
    },

    async set(key, value) {
      try {
        backend.write(ns(key), JSON.stringify(value));
      } catch (err) {
        console.warn('[storage] write failed', err);
      }
    },

    async remove(key) {
      try {
        backend.delete(ns(key));
      } catch (err) {
        console.warn('[storage] remove failed', err);
      }
    },
  };
}
